import React, { useState, useEffect } from "react";
import { C, getPerms, hasAnyRole } from "./constants";
import {
  Card, Btn, Field, Modal, KPICard, TabBar, ModuleHeader, useToast, TransactionsTab, MeetingsTab
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  getGrantDonors, createGrantDonor, updateGrantDonor, deleteGrantDonor,
  getGrants, createGrant, updateGrant, deleteGrant,
  getGrantReports, createGrantReport, updateGrantReport,
} from "./supabaseClient";
import {
  Building2, FolderOpen, FileText, CalendarClock, Paperclip, BarChart3,
  Plus, Search, Edit, Trash2, AlertTriangle, Clock,
  CheckCircle, TrendingUp, DollarSign, ArrowLeft,
  Phone, Mail, Globe, User, Calendar, Flag
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

// صف grants → شكل الواجهة (snake_case → camelCase)
function mapGrantRow(r) {
  return {
    id: r.id,
    donorId: r.donor_id || "",
    donorName: r.donor_name || "",
    projectName: r.project_name || "",
    grantNumber: r.grant_number || "",
    approvalDate: r.approval_date || "",
    amount: Number(r.amount) || 0,
    spent: Number(r.spent) || 0,
    remaining: Number(r.remaining) || 0,
    duration: r.duration || "",
    startDate: r.start_date || "",
    endDate: r.end_date || "",
    status: r.status || "قيد التنفيذ",
    progress: Number(r.progress) || 0,
    manager: r.manager || "",
    conditions: r.conditions || "",
    notes: r.notes || "",
  };
}

// صف grant_reports → شكل الواجهة
function mapGrantReportRow(r) {
  return {
    id: r.id,
    grantId: r.grant_id || "",
    projectName: r.project_name || "",
    type: r.type || "",
    dueDate: r.due_date || "",
    submitDate: r.submit_date || "",
    status: r.status || "لم يبدأ",
    link: r.link || "",
    donorNotes: r.donor_notes || "",
    responsible: r.responsible || "",
  };
}

// صف grant_donors → شكل واجهة الجهة المانحة (snake_case → camelCase)
function mapGrantDonorRow(r) {
  return {
    id: r.id,
    name: r.name || "",
    type: r.type || "",
    status: r.status || "نشطة",
    contact: r.contact || "",
    phone: r.phone || "",
    email: r.email || "",
    website: r.website || "",
    lastContact: r.last_contact || "",
    nextFollowUp: r.next_follow_up || "",
    notes: r.notes || "",
    attachments: Array.isArray(r.attachments) ? r.attachments : [],
    totalGrants: Number(r.total_grants) || 0,
    totalAmount: Number(r.total_amount) || 0,
  };
}

const STATUS_COLORS = {
  "نشطة": C.success, "متوقفة": C.danger, "محتملة": C.accent, "تحتاج متابعة": "#e67e22",
};

const PROJECT_STATUS_COLORS = {
  "قيد التنفيذ": C.teal, "مكتمل": C.success, "متأخر": "#e67e22", "متوقف": C.danger, "مغلق": "#6b7280",
};

const REPORT_STATUS_COLORS = {
  "لم يبدأ": "#6b7280", "قيد الإعداد": C.accent, "مرفوع": C.teal, "معتمد": C.success, "يحتاج تعديل": "#e67e22", "متأخر": C.danger,
};

const DONOR_TYPES = ["مؤسسة مانحة", "منصة", "جهة حكومية", "شركة", "فرد داعم"];
const DONOR_STATUSES = ["نشطة", "متوقفة", "محتملة", "تحتاج متابعة"];
const PROJECT_STATUSES = ["قيد التنفيذ", "مكتمل", "متأخر", "متوقف", "مغلق"];
const REPORT_TYPES = ["مرحلي", "شهري", "ربعي", "ختامي", "مالي", "تشغيلي"];
const REPORT_STATUSES = ["لم يبدأ", "قيد الإعداد", "مرفوع", "معتمد", "يحتاج تعديل", "متأخر"];

const CHART_COLORS = [C.teal, C.accent, C.primary, C.success, "#e67e22", C.danger, "#7c3aed", "#2563eb"];

// ===== Helper: Status Badge =====
function StatusBadge({ status, colorMap }) {
  const color = colorMap[status] || "#6b7280";
  return (
    <span style={{
      display: "inline-block", padding: "4px 12px", borderRadius: 8,
      background: `${color}15`, color, fontWeight: 600, fontSize: 12,
    }}>
      {status}
    </span>
  );
}

// ===== Helper: Progress Bar =====
function ProgressBar({ value, height = 8 }) {
  const color = value >= 100 ? C.success : value >= 60 ? C.teal : value >= 30 ? C.accent : C.danger;
  return (
    <div style={{ width: "100%", height, borderRadius: height, background: `${C.border}`, overflow: "hidden" }}>
      <div style={{ width: `${Math.min(value, 100)}%`, height: "100%", borderRadius: height, background: color, transition: "width 0.4s ease" }} />
    </div>
  );
}

// ===== Main Component =====
export default function GrantsPage({ user, initialTab }) {
  const [tab, setTab] = useState("overview");
  const [donors, setDonors] = useState([]);
  const [grants, setGrants] = useState([]);
  const [reports, setReports] = useState([]);
  const [showForm, setShowForm] = useState(null); // "donor" | "grant" | "report" | null
  const [editItem, setEditItem] = useState(null);
  const [viewDonor, setViewDonor] = useState(null);
  const [viewGrant, setViewGrant] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showToast, ToastView] = useToast();
  const perms = getPerms(user);

  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);

  // الجهات المانحة الحيّة من grant_donors — لا mock
  useEffect(() => {
    let alive = true;
    getGrantDonors()
      .then(rows => { if (alive) setDonors((rows || []).map(mapGrantDonorRow)); })
      .catch(() => { if (alive) setDonors([]); });
    return () => { alive = false; };
  }, []);

  // المنح والتقارير الحيّة — لا mock
  useEffect(() => {
    let alive = true;
    getGrants()
      .then(rows => { if (alive) setGrants((rows || []).map(mapGrantRow)); })
      .catch(() => { if (alive) setGrants([]); });
    getGrantReports()
      .then(rows => { if (alive) setReports((rows || []).map(mapGrantReportRow)); })
      .catch(() => { if (alive) setReports([]); });
    return () => { alive = false; };
  }, []);

  const tabs = [
    { key: "overview", label: "الإحصائيات", icon: BarChart3 },
    { key: "donors", label: "الجهات المانحة", icon: Building2 },
    { key: "grants", label: "المنح والمشاريع", icon: FolderOpen },
    { key: "reports", label: "التقارير", icon: FileText },
    { key: "followups", label: "المواعيد والمتابعات", icon: CalendarClock },
    { key: "attachments", label: "المرفقات", icon: Paperclip },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "finance", label: "الشؤون المالية", icon: DollarSign },
  ];

  const canEdit = perms.canEdit || hasAnyRole(user, ["exec", "secretary", "system", "fundraising_manager"]);

  const money = (v) => (typeof v === "number" ? v.toLocaleString("ar-SA") : (v ?? "—"));
  const reportSources = [
    {
      label: "المنح", mode: "monthly", dateField: "approvalDate", rows: grants,
      columns: [
        { key: "grantNumber", label: "رقم المنحة" },
        { key: "donorName", label: "الجهة المانحة" },
        { key: "projectName", label: "المشروع" },
        { key: "approvalDate", label: "تاريخ الاعتماد" },
        { key: "amount", label: "المبلغ", format: money },
        { key: "status", label: "الحالة" },
      ],
    },
    {
      label: "التقارير", mode: "monthly", dateField: "dueDate", rows: reports,
      columns: [
        { key: "projectName", label: "المشروع" },
        { key: "type", label: "النوع" },
        { key: "dueDate", label: "تاريخ الاستحقاق" },
        { key: "submitDate", label: "تاريخ الرفع" },
        { key: "status", label: "الحالة" },
        { key: "responsible", label: "المسؤول" },
      ],
    },
    { label: "الجهات المانحة", mode: "cumulative", rows: donors },
  ];

  // ===== Statistics =====
  const totalDonors = donors.length;
  const activeDonors = donors.filter(d => d.status === "نشطة").length;
  const totalGrantsCount = grants.length;
  const totalAmount = grants.reduce((s, g) => s + g.amount, 0);
  const inProgress = grants.filter(g => g.status === "قيد التنفيذ").length;
  const dueReports = reports.filter(r => ["لم يبدأ", "قيد الإعداد"].includes(r.status)).length;
  const lateReports = reports.filter(r => r.status === "متأخر").length;
  const closedGrants = grants.filter(g => g.status === "مكتمل" || g.status === "مغلق").length;

  // ===== Donor Form =====
  const emptyDonor = { name: "", type: "", contact: "", phone: "", email: "", website: "", status: "نشطة", notes: "", lastContact: new Date().toISOString().split("T")[0], nextFollowUp: "", attachments: [], totalGrants: 0, totalAmount: 0 };
  const [donorForm, setDonorForm] = useState(emptyDonor);

  const emptyGrant = { donorId: "", donorName: "", projectName: "", grantNumber: "", approvalDate: "", amount: 0, spent: 0, remaining: 0, duration: "", startDate: "", endDate: "", status: "قيد التنفيذ", progress: 0, manager: "", conditions: "", notes: "" };
  const [grantForm, setGrantForm] = useState(emptyGrant);

  const emptyReport = { grantId: "", projectName: "", type: "", dueDate: "", submitDate: "", status: "لم يبدأ", link: "", donorNotes: "", responsible: "" };
  const [reportForm, setReportForm] = useState(emptyReport);

  async function handleSaveDonor() {
    if (!donorForm.name || !donorForm.type) { showToast("يرجى تعبئة اسم الجهة ونوعها", "error"); return; }
    // total_grants / total_amount لا تُدخَل من النموذج (DEFAULT 0، تُحدّث من المنح لاحقًا)
    const dbRow = {
      name: donorForm.name,
      type: donorForm.type,
      status: donorForm.status || "نشطة",
      contact: donorForm.contact || "",
      phone: donorForm.phone || "",
      email: donorForm.email || "",
      website: donorForm.website || "",
      last_contact: donorForm.lastContact || null,
      next_follow_up: donorForm.nextFollowUp || null,
      notes: donorForm.notes || null,
    };
    try {
      if (editItem) {
        const updated = await updateGrantDonor(editItem.id, dbRow);
        const mapped = updated ? mapGrantDonorRow(updated) : { ...donorForm, id: editItem.id };
        setDonors(prev => prev.map(d => d.id === editItem.id ? mapped : d));
        showToast("تم تحديث بيانات الجهة المانحة");
      } else {
        const created = await createGrantDonor(dbRow);
        if (created) setDonors(prev => [mapGrantDonorRow(created), ...prev]);
        showToast("تمت إضافة جهة مانحة جديدة");
      }
      setShowForm(null); setEditItem(null); setDonorForm(emptyDonor);
    } catch (e) {
      showToast(e.message || "تعذّر حفظ الجهة المانحة", "error");
    }
  }

  async function handleSaveGrant() {
    if (!grantForm.projectName || !grantForm.donorId) { showToast("يرجى تعبئة اسم المشروع واختيار الجهة المانحة", "error"); return; }
    const donor = donors.find(d => String(d.id) === String(grantForm.donorId));
    const remaining = (Number(grantForm.amount) || 0) - (Number(grantForm.spent) || 0);
    const dbRow = {
      donor_id: grantForm.donorId || null,
      donor_name: donor?.name || "",
      project_name: grantForm.projectName,
      grant_number: grantForm.grantNumber || "",
      approval_date: grantForm.approvalDate || null,
      amount: Number(grantForm.amount) || 0,
      spent: Number(grantForm.spent) || 0,
      remaining,
      duration: grantForm.duration || "",
      start_date: grantForm.startDate || null,
      end_date: grantForm.endDate || null,
      status: grantForm.status || "قيد التنفيذ",
      progress: Number(grantForm.progress) || 0,
      manager: grantForm.manager || "",
      conditions: grantForm.conditions || "",
      notes: grantForm.notes || "",
    };
    try {
      if (editItem) {
        const updated = await updateGrant(editItem.id, dbRow);
        const mapped = updated ? mapGrantRow(updated) : { ...grantForm, id: editItem.id, donorName: donor?.name || "", remaining };
        setGrants(prev => prev.map(g => g.id === editItem.id ? mapped : g));
        showToast("تم تحديث بيانات المنحة");
      } else {
        const created = await createGrant(dbRow);
        if (created) setGrants(prev => [mapGrantRow(created), ...prev]);
        showToast("تمت إضافة منحة جديدة");
      }
      setShowForm(null); setEditItem(null); setGrantForm(emptyGrant);
    } catch (e) {
      showToast(e.message || "تعذّر حفظ المنحة", "error");
    }
  }

  async function handleSaveReport() {
    if (!reportForm.grantId || !reportForm.type || !reportForm.dueDate) { showToast("يرجى تعبئة البيانات المطلوبة", "error"); return; }
    const grant = grants.find(g => String(g.id) === String(reportForm.grantId));
    const dbRow = {
      grant_id: reportForm.grantId || null,
      project_name: grant?.projectName || "",
      type: reportForm.type || "",
      due_date: reportForm.dueDate || null,
      submit_date: reportForm.submitDate || null,
      status: reportForm.status || "لم يبدأ",
      link: reportForm.link || "",
      donor_notes: reportForm.donorNotes || "",
      responsible: reportForm.responsible || "",
    };
    try {
      if (editItem) {
        const updated = await updateGrantReport(editItem.id, dbRow);
        const mapped = updated ? mapGrantReportRow(updated) : { ...reportForm, id: editItem.id, projectName: grant?.projectName || "" };
        setReports(prev => prev.map(r => r.id === editItem.id ? mapped : r));
        showToast("تم تحديث التقرير");
      } else {
        const created = await createGrantReport(dbRow);
        if (created) setReports(prev => [mapGrantReportRow(created), ...prev]);
        showToast("تمت إضافة تقرير جديد");
      }
      setShowForm(null); setEditItem(null); setReportForm(emptyReport);
    } catch (e) {
      showToast(e.message || "تعذّر حفظ التقرير", "error");
    }
  }

  async function handleDeleteDonor(id) {
    if (!window.confirm("هل أنت متأكد من حذف هذه الجهة المانحة؟")) return;
    try {
      await deleteGrantDonor(id);
      setDonors(prev => prev.filter(d => d.id !== id));
      showToast("تم حذف الجهة المانحة");
    } catch (e) {
      showToast(e.message || "تعذّر حذف الجهة المانحة", "error");
    }
  }

  async function handleDeleteGrant(id) {
    if (!window.confirm("هل أنت متأكد من حذف هذه المنحة؟")) return;
    try {
      await deleteGrant(id);
      setGrants(prev => prev.filter(g => g.id !== id));
      showToast("تم حذف المنحة");
    } catch (e) {
      showToast(e.message || "تعذّر حذف المنحة", "error");
    }
  }

  // ===== Alerts =====
  const today = new Date().toISOString().split("T")[0];
  const alerts = [
    ...reports.filter(r => r.status === "متأخر").map(r => ({ type: "danger", icon: AlertTriangle, msg: `تقرير متأخر: ${r.projectName} — ${r.type}` })),
    ...reports.filter(r => r.dueDate && r.dueDate <= new Date(Date.now() + 7 * 86400000).toISOString().split("T")[0] && ["لم يبدأ", "قيد الإعداد"].includes(r.status)).map(r => ({ type: "warning", icon: Clock, msg: `تقرير مستحق قريباً: ${r.projectName} — ${r.type} (${r.dueDate})` })),
    ...grants.filter(g => g.endDate && g.endDate <= new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0] && g.status === "قيد التنفيذ").map(g => ({ type: "warning", icon: Calendar, msg: `مشروع قارب على الانتهاء: ${g.projectName} (${g.endDate})` })),
    ...donors.filter(d => d.nextFollowUp && d.nextFollowUp <= today).map(d => ({ type: "info", icon: Phone, msg: `موعد متابعة: ${d.name} (${d.nextFollowUp})` })),
    ...reports.filter(r => r.status === "يحتاج تعديل").map(r => ({ type: "warning", icon: Edit, msg: `تقرير يحتاج تعديل: ${r.projectName} — ${r.type}` })),
  ];

  // ===== Charts Data =====
  const donorsByType = DONOR_TYPES.map(t => ({ name: t, value: donors.filter(d => d.type === t).length })).filter(d => d.value > 0);
  const grantsByStatus = PROJECT_STATUSES.map(s => ({ name: s, value: grants.filter(g => g.status === s).length })).filter(d => d.value > 0);
  const grantsByDonor = donors.filter(d => d.totalAmount > 0 || grants.filter(g => g.donorName === d.name).length > 0).map(d => ({
    name: d.name.length > 20 ? d.name.substring(0, 20) + "..." : d.name,
    amount: grants.filter(g => g.donorName === d.name).reduce((s, g) => s + g.amount, 0),
  })).sort((a, b) => b.amount - a.amount);

  // ===== RENDER TABS =====

  // ----------- Overview -----------
  function renderOverview() {
    return (
      <div>
        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
          <KPICard title="إجمالي الجهات المانحة" value={totalDonors} icon={Building2} color={C.primary} />
          <KPICard title="الجهات النشطة" value={activeDonors} icon={CheckCircle} color={C.success} />
          <KPICard title="إجمالي المنح" value={totalGrantsCount} icon={FolderOpen} color={C.teal} />
          <KPICard title="إجمالي مبالغ المنح" value={`${(totalAmount / 1000).toFixed(0)}K`} icon={DollarSign} color={C.accent} subtitle="ريال سعودي" />
          <KPICard title="مشاريع قيد التنفيذ" value={inProgress} icon={TrendingUp} color={C.teal} />
          <KPICard title="تقارير مستحقة" value={dueReports} icon={FileText} color="#e67e22" />
          <KPICard title="تقارير متأخرة" value={lateReports} icon={AlertTriangle} color={C.danger} />
          <KPICard title="منح مغلقة / مكتملة" value={closedGrants} icon={Flag} color="#6b7280" />
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <Card style={{ marginBottom: 24, padding: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px", display: "flex", alignItems: "center", gap: 8 }}>
              <AlertTriangle size={18} color={C.danger} /> التنبيهات ({alerts.length})
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {alerts.slice(0, 6).map((a, i) => {
                const AIcon = a.icon;
                const color = a.type === "danger" ? C.danger : a.type === "warning" ? "#e67e22" : C.teal;
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, background: `${color}08`, borderRight: `3px solid ${color}` }}>
                    <AIcon size={16} color={color} />
                    <span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{a.msg}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Charts */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 20 }}>
          {/* Donors by Type */}
          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: C.primary, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
              <Building2 size={18} color={C.teal} /> الجهات المانحة حسب النوع
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={donorsByType} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {donorsByType.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => v} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginTop: 8 }}>
              {donorsByType.map((d, i) => (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: C.textLight }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  {d.name} ({d.value})
                </span>
              ))}
            </div>
          </Card>

          {/* Grants by Status */}
          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: C.primary, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
              <FolderOpen size={18} color={C.teal} /> المنح حسب الحالة
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={grantsByStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {grantsByStatus.map((entry, i) => <Cell key={i} fill={PROJECT_STATUS_COLORS[entry.name] || CHART_COLORS[i]} />)}
                </Pie>
                <Tooltip formatter={(v) => v} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginTop: 8 }}>
              {grantsByStatus.map((d, i) => (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: C.textLight }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: PROJECT_STATUS_COLORS[d.name] || CHART_COLORS[i] }} />
                  {d.name} ({d.value})
                </span>
              ))}
            </div>
          </Card>

          {/* Grants by Donor */}
          <Card style={{ gridColumn: "1 / -1" }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: C.primary, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
              <DollarSign size={18} color={C.teal} /> مبالغ المنح حسب الجهة (ريال)
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={grantsByDonor} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis type="number" tickFormatter={v => `${(v/1000).toFixed(0)}K`} style={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" width={160} style={{ fontSize: 11 }} tick={{ fill: C.textLight }} />
                <Tooltip formatter={v => `${v.toLocaleString("ar-SA")} ريال`} />
                <Bar dataKey="amount" fill={C.teal} radius={[0, 6, 6, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </div>
    );
  }

  // ----------- Donors Tab -----------
  function renderDonors() {
    const filtered = donors.filter(d => {
      if (searchQuery && !d.name.includes(searchQuery) && !d.contact.includes(searchQuery)) return false;
      if (filterStatus && d.status !== filterStatus) return false;
      if (filterType && d.type !== filterType) return false;
      return true;
    });

    if (viewDonor) {
      const d = viewDonor;
      const donorGrants = grants.filter(g => g.donorName === d.name);
      return (
        <div>
          <Btn variant="ghost" icon={ArrowLeft} onClick={() => setViewDonor(null)} size="sm" style={{ marginBottom: 16 }}>رجوع</Btn>
          <Card style={{ marginBottom: 20, padding: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.primary }}>{d.name}</h2>
                <StatusBadge status={d.status} colorMap={STATUS_COLORS} />
              </div>
              {canEdit && (
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn variant="ghost" icon={Edit} size="sm" onClick={() => { setDonorForm(d); setEditItem(d); setShowForm("donor"); setViewDonor(null); }}>تعديل</Btn>
                </div>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 16, marginTop: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><User size={16} color={C.textLight} /><span style={{ fontSize: 13, color: C.textLight }}>مسؤول التواصل:</span><strong style={{ fontSize: 13 }}>{d.contact}</strong></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Phone size={16} color={C.textLight} /><span style={{ fontSize: 13, color: C.textLight }}>الجوال:</span><strong style={{ fontSize: 13 }}>{d.phone}</strong></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Mail size={16} color={C.textLight} /><span style={{ fontSize: 13, color: C.textLight }}>البريد:</span><strong style={{ fontSize: 13 }}>{d.email || "—"}</strong></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Globe size={16} color={C.textLight} /><span style={{ fontSize: 13, color: C.textLight }}>الموقع:</span><strong style={{ fontSize: 13 }}>{d.website || "—"}</strong></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Building2 size={16} color={C.textLight} /><span style={{ fontSize: 13, color: C.textLight }}>النوع:</span><strong style={{ fontSize: 13 }}>{d.type}</strong></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Calendar size={16} color={C.textLight} /><span style={{ fontSize: 13, color: C.textLight }}>آخر تواصل:</span><strong style={{ fontSize: 13 }}>{d.lastContact}</strong></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><CalendarClock size={16} color={C.textLight} /><span style={{ fontSize: 13, color: C.textLight }}>المتابعة القادمة:</span><strong style={{ fontSize: 13 }}>{d.nextFollowUp || "—"}</strong></div>
            </div>
            {d.notes && <p style={{ margin: "16px 0 0", padding: "12px 16px", borderRadius: 10, background: `${C.teal}06`, fontSize: 13, color: C.text, borderRight: `3px solid ${C.teal}` }}>{d.notes}</p>}
            {d.attachments && d.attachments.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: C.textLight, margin: "0 0 8px" }}>المرفقات:</h4>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {d.attachments.map((a, i) => (
                    <span key={i} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 8, background: `${C.primary}08`, fontSize: 12, color: C.primary, fontWeight: 500 }}>
                      <Paperclip size={13} /> {a}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Donor's Grants */}
          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px", display: "flex", alignItems: "center", gap: 8 }}>
            <FolderOpen size={18} color={C.teal} /> المنح والمشاريع ({donorGrants.length})
          </h3>
          {donorGrants.length === 0 ? (
            <Card><p style={{ textAlign: "center", color: C.textLight, fontSize: 14, padding: 20 }}>لا توجد منح مسجلة لهذه الجهة</p></Card>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {donorGrants.map(g => (
                <Card key={g.id} style={{ padding: 20, cursor: "pointer" }} onClick={() => { setViewGrant(g); setViewDonor(null); }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary }}>{g.projectName}</h4>
                      {g.grantNumber && <span style={{ fontSize: 12, color: C.textLight }}>رقم المنحة: {g.grantNumber}</span>}
                    </div>
                    <StatusBadge status={g.status} colorMap={PROJECT_STATUS_COLORS} />
                  </div>
                  <div style={{ display: "flex", gap: 20, marginTop: 12, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, color: C.textLight }}>المبلغ: <strong style={{ color: C.primary }}>{g.amount.toLocaleString("ar-SA")} ريال</strong></span>
                    <span style={{ fontSize: 13, color: C.textLight }}>المصروف: <strong>{g.spent.toLocaleString("ar-SA")}</strong></span>
                    <span style={{ fontSize: 13, color: C.textLight }}>المتبقي: <strong style={{ color: C.success }}>{g.remaining.toLocaleString("ar-SA")}</strong></span>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: C.textLight }}>نسبة الإنجاز</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.primary }}>{g.progress}%</span>
                    </div>
                    <ProgressBar value={g.progress} />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        {/* Actions Bar */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18, alignItems: "center" }}>
          {canEdit && <Btn icon={Plus} onClick={() => { setDonorForm(emptyDonor); setEditItem(null); setShowForm("donor"); }}>إضافة جهة مانحة</Btn>}
          <div style={{ flex: 1 }} />
          <div style={{ position: "relative" }}>
            <Search size={16} color={C.textLight} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }} />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="بحث..." style={{ padding: "9px 38px 9px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "Readex Pro, sans-serif", width: 200 }} />
          </div>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: "9px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "Readex Pro, sans-serif" }}>
            <option value="">كل الأنواع</option>
            {DONOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: "9px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "Readex Pro, sans-serif" }}>
            <option value="">كل الحالات</option>
            {DONOR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Donor Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
          {filtered.map(d => {
            const donorGrantsList = grants.filter(g => g.donorName === d.name);
            const donorTotal = donorGrantsList.reduce((s, g) => s + g.amount, 0);
            const nearestReport = reports.filter(r => donorGrantsList.some(g => g.id === r.grantId) && !["معتمد", "مغلق"].includes(r.status)).sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""))[0];
            return (
              <Card key={d.id} style={{ cursor: "pointer", padding: 22 }} onClick={() => setViewDonor(d)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.primary, lineHeight: 1.4 }}>{d.name}</h3>
                    <span style={{ fontSize: 12, color: C.textLight }}>{d.type}</span>
                  </div>
                  <StatusBadge status={d.status} colorMap={STATUS_COLORS} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 13 }}>
                  <div><span style={{ color: C.textLight }}>إجمالي الدعم:</span><br /><strong style={{ color: C.primary }}>{donorTotal > 0 ? `${donorTotal.toLocaleString("ar-SA")} ريال` : "—"}</strong></div>
                  <div><span style={{ color: C.textLight }}>المشاريع:</span><br /><strong style={{ color: C.primary }}>{donorGrantsList.length}</strong></div>
                  <div><span style={{ color: C.textLight }}>أقرب تقرير:</span><br /><strong style={{ fontSize: 12, color: nearestReport ? "#e67e22" : C.textLight }}>{nearestReport ? `${nearestReport.type} (${nearestReport.dueDate})` : "—"}</strong></div>
                  <div><span style={{ color: C.textLight }}>آخر تواصل:</span><br /><strong>{d.lastContact}</strong></div>
                </div>
                {canEdit && (
                  <div style={{ display: "flex", gap: 6, marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12 }} onClick={e => e.stopPropagation()}>
                    <Btn variant="ghost" size="sm" icon={Edit} onClick={() => { setDonorForm(d); setEditItem(d); setShowForm("donor"); }}>تعديل</Btn>
                    <Btn variant="ghost" size="sm" icon={Trash2} onClick={() => handleDeleteDonor(d.id)} style={{ color: C.danger }}>حذف</Btn>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
        {filtered.length === 0 && <Card><p style={{ textAlign: "center", color: C.textLight, padding: 30, fontSize: 14 }}>لا توجد نتائج مطابقة</p></Card>}
      </div>
    );
  }

  // ----------- Grants Tab -----------
  function renderGrants() {
    const filteredGrants = grants.filter(g => {
      if (searchQuery && !g.projectName.includes(searchQuery) && !g.donorName.includes(searchQuery)) return false;
      if (filterStatus && g.status !== filterStatus) return false;
      return true;
    });

    if (viewGrant) {
      const g = viewGrant;
      const grantReports = reports.filter(r => r.grantId === g.id);
      return (
        <div>
          <Btn variant="ghost" icon={ArrowLeft} onClick={() => setViewGrant(null)} size="sm" style={{ marginBottom: 16 }}>رجوع</Btn>
          <Card style={{ padding: 28, marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.primary }}>{g.projectName}</h2>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textLight }}>{g.donorName} {g.grantNumber && `— رقم المنحة: ${g.grantNumber}`}</p>
              </div>
              <StatusBadge status={g.status} colorMap={PROJECT_STATUS_COLORS} />
            </div>

            {/* Financial Summary */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 14, marginTop: 20 }}>
              <div style={{ padding: "14px 18px", borderRadius: 12, background: `${C.primary}06`, textAlign: "center" }}>
                <p style={{ margin: 0, fontSize: 11, color: C.textLight }}>مبلغ المنحة</p>
                <p style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 700, color: C.primary }}>{g.amount.toLocaleString("ar-SA")}</p>
              </div>
              <div style={{ padding: "14px 18px", borderRadius: 12, background: `${C.teal}06`, textAlign: "center" }}>
                <p style={{ margin: 0, fontSize: 11, color: C.textLight }}>المصروف</p>
                <p style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 700, color: C.teal }}>{g.spent.toLocaleString("ar-SA")}</p>
              </div>
              <div style={{ padding: "14px 18px", borderRadius: 12, background: `${C.success}06`, textAlign: "center" }}>
                <p style={{ margin: 0, fontSize: 11, color: C.textLight }}>المتبقي</p>
                <p style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 700, color: C.success }}>{g.remaining.toLocaleString("ar-SA")}</p>
              </div>
              <div style={{ padding: "14px 18px", borderRadius: 12, background: `${C.accent}10`, textAlign: "center" }}>
                <p style={{ margin: 0, fontSize: 11, color: C.textLight }}>نسبة الإنجاز</p>
                <p style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 700, color: C.primary }}>{g.progress}%</p>
              </div>
            </div>

            <div style={{ marginTop: 16 }}><ProgressBar value={g.progress} height={10} /></div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 14, marginTop: 20 }}>
              <div style={{ fontSize: 13 }}><span style={{ color: C.textLight }}>مدة التنفيذ:</span> <strong>{g.duration}</strong></div>
              <div style={{ fontSize: 13 }}><span style={{ color: C.textLight }}>بداية المشروع:</span> <strong>{g.startDate}</strong></div>
              <div style={{ fontSize: 13 }}><span style={{ color: C.textLight }}>نهاية المشروع:</span> <strong>{g.endDate}</strong></div>
              <div style={{ fontSize: 13 }}><span style={{ color: C.textLight }}>مسؤول المشروع:</span> <strong>{g.manager}</strong></div>
            </div>
            {g.conditions && <p style={{ margin: "16px 0 0", padding: "10px 14px", borderRadius: 10, background: `${C.accent}08`, fontSize: 13, borderRight: `3px solid ${C.accent}` }}><strong>شروط الجهة المانحة:</strong> {g.conditions}</p>}
            {g.notes && <p style={{ margin: "10px 0 0", padding: "10px 14px", borderRadius: 10, background: `${C.teal}06`, fontSize: 13, borderRight: `3px solid ${C.teal}` }}><strong>ملاحظات:</strong> {g.notes}</p>}
          </Card>

          {/* Grant Reports */}
          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px", display: "flex", alignItems: "center", gap: 8 }}>
            <FileText size={18} color={C.teal} /> تقارير المشروع ({grantReports.length})
          </h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: `${C.primary}06` }}>
                  {["النوع", "تاريخ الاستحقاق", "تاريخ الرفع", "الحالة", "المسؤول", "ملاحظات الجهة"].map(h => (
                    <th key={h} style={{ padding: "12px 14px", textAlign: "right", fontSize: 12, fontWeight: 600, color: C.textLight, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grantReports.map(r => (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "12px 14px", fontSize: 13 }}>{r.type}</td>
                    <td style={{ padding: "12px 14px", fontSize: 13 }}>{r.dueDate}</td>
                    <td style={{ padding: "12px 14px", fontSize: 13 }}>{r.submitDate || "—"}</td>
                    <td style={{ padding: "12px 14px" }}><StatusBadge status={r.status} colorMap={REPORT_STATUS_COLORS} /></td>
                    <td style={{ padding: "12px 14px", fontSize: 13 }}>{r.responsible}</td>
                    <td style={{ padding: "12px 14px", fontSize: 12, color: C.textLight }}>{r.donorNotes || "—"}</td>
                  </tr>
                ))}
                {grantReports.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 20, color: C.textLight }}>لا توجد تقارير</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18, alignItems: "center" }}>
          {canEdit && <Btn icon={Plus} onClick={() => { setGrantForm(emptyGrant); setEditItem(null); setShowForm("grant"); }}>إضافة منحة / مشروع</Btn>}
          <div style={{ flex: 1 }} />
          <div style={{ position: "relative" }}>
            <Search size={16} color={C.textLight} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }} />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="بحث..." style={{ padding: "9px 38px 9px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "Readex Pro, sans-serif", width: 200 }} />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: "9px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "Readex Pro, sans-serif" }}>
            <option value="">كل الحالات</option>
            {PROJECT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          {filteredGrants.map(g => (
            <Card key={g.id} style={{ padding: 20, cursor: "pointer" }} onClick={() => setViewGrant(g)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary }}>{g.projectName}</h4>
                  <span style={{ fontSize: 12, color: C.textLight }}>{g.donorName} {g.grantNumber && `— ${g.grantNumber}`}</span>
                </div>
                <StatusBadge status={g.status} colorMap={PROJECT_STATUS_COLORS} />
              </div>
              <div style={{ display: "flex", gap: 20, marginTop: 12, flexWrap: "wrap", fontSize: 13 }}>
                <span style={{ color: C.textLight }}>المبلغ: <strong style={{ color: C.primary }}>{g.amount.toLocaleString("ar-SA")}</strong></span>
                <span style={{ color: C.textLight }}>المصروف: <strong>{g.spent.toLocaleString("ar-SA")}</strong></span>
                <span style={{ color: C.textLight }}>الإنجاز: <strong>{g.progress}%</strong></span>
                <span style={{ color: C.textLight }}>المسؤول: <strong>{g.manager}</strong></span>
              </div>
              <div style={{ marginTop: 10 }}><ProgressBar value={g.progress} /></div>
              {canEdit && (
                <div style={{ display: "flex", gap: 6, marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 10 }} onClick={e => e.stopPropagation()}>
                  <Btn variant="ghost" size="sm" icon={Edit} onClick={() => { setGrantForm(g); setEditItem(g); setShowForm("grant"); }}>تعديل</Btn>
                  <Btn variant="ghost" size="sm" icon={Trash2} onClick={() => handleDeleteGrant(g.id)} style={{ color: C.danger }}>حذف</Btn>
                </div>
              )}
            </Card>
          ))}
        </div>
        {filteredGrants.length === 0 && <Card><p style={{ textAlign: "center", color: C.textLight, padding: 30, fontSize: 14 }}>لا توجد نتائج مطابقة</p></Card>}
      </div>
    );
  }

  // ----------- Reports Tab -----------
  function renderReports() {
    const filteredReports = reports.filter(r => {
      if (searchQuery && !r.projectName.includes(searchQuery) && !r.responsible.includes(searchQuery)) return false;
      if (filterStatus && r.status !== filterStatus) return false;
      if (filterType && r.type !== filterType) return false;
      return true;
    });

    return (
      <div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18, alignItems: "center" }}>
          {canEdit && <Btn icon={Plus} onClick={() => { setReportForm(emptyReport); setEditItem(null); setShowForm("report"); }}>إضافة تقرير</Btn>}
          <div style={{ flex: 1 }} />
          <div style={{ position: "relative" }}>
            <Search size={16} color={C.textLight} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }} />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="بحث..." style={{ padding: "9px 38px 9px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "Readex Pro, sans-serif", width: 200 }} />
          </div>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: "9px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "Readex Pro, sans-serif" }}>
            <option value="">كل الأنواع</option>
            {REPORT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: "9px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "Readex Pro, sans-serif" }}>
            <option value="">كل الحالات</option>
            {REPORT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: `${C.primary}06` }}>
                {["المشروع", "نوع التقرير", "تاريخ الاستحقاق", "تاريخ الرفع", "الحالة", "المسؤول", "المرفق", "ملاحظات الجهة"].map(h => (
                  <th key={h} style={{ padding: "12px 14px", textAlign: "right", fontSize: 12, fontWeight: 600, color: C.textLight, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
                {canEdit && <th style={{ padding: "12px 14px", textAlign: "right", fontSize: 12, fontWeight: 600, color: C.textLight, borderBottom: `1px solid ${C.border}` }}>إجراء</th>}
              </tr>
            </thead>
            <tbody>
              {filteredReports.map(r => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600 }}>{r.projectName}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13 }}>{r.type}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13 }}>{r.dueDate}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13 }}>{r.submitDate || "—"}</td>
                  <td style={{ padding: "12px 14px" }}><StatusBadge status={r.status} colorMap={REPORT_STATUS_COLORS} /></td>
                  <td style={{ padding: "12px 14px", fontSize: 13 }}>{r.responsible}</td>
                  <td style={{ padding: "12px 14px", fontSize: 12 }}>{r.link ? <span style={{ color: C.teal, fontWeight: 500 }}><Paperclip size={12} /> {r.link}</span> : "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 12, color: C.textLight, maxWidth: 200 }}>{r.donorNotes || "—"}</td>
                  {canEdit && (
                    <td style={{ padding: "12px 14px" }}>
                      <Btn variant="ghost" size="sm" icon={Edit} onClick={() => { setReportForm(r); setEditItem(r); setShowForm("report"); }}>تعديل</Btn>
                    </td>
                  )}
                </tr>
              ))}
              {filteredReports.length === 0 && <tr><td colSpan={9} style={{ textAlign: "center", padding: 20, color: C.textLight }}>لا توجد نتائج</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ----------- Follow-ups Tab -----------
  function renderFollowUps() {
    const upcoming = [
      ...donors.filter(d => d.nextFollowUp).map(d => ({ type: "متابعة جهة", date: d.nextFollowUp, title: d.name, subtitle: `مسؤول التواصل: ${d.contact}`, icon: Phone, color: C.teal })),
      ...reports.filter(r => r.dueDate && !["معتمد"].includes(r.status)).map(r => ({ type: "تقرير مستحق", date: r.dueDate, title: r.projectName, subtitle: `${r.type} — ${r.responsible}`, icon: FileText, color: r.status === "متأخر" ? C.danger : "#e67e22" })),
      ...grants.filter(g => g.endDate && g.status === "قيد التنفيذ").map(g => ({ type: "نهاية مشروع", date: g.endDate, title: g.projectName, subtitle: `${g.donorName} — ${g.manager}`, icon: Flag, color: C.primary })),
    ].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

    return (
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 18px", display: "flex", alignItems: "center", gap: 8 }}>
          <CalendarClock size={18} color={C.teal} /> المواعيد والمتابعات القادمة ({upcoming.length})
        </h3>
        <div style={{ display: "grid", gap: 10 }}>
          {upcoming.map((item, i) => {
            const IIcon = item.icon;
            const isOverdue = item.date < today;
            return (
              <Card key={i} style={{ padding: 18, borderRight: `4px solid ${isOverdue ? C.danger : item.color}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: `${item.color}12`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <IIcon size={20} color={item.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.primary }}>{item.title}</h4>
                      <span style={{ fontSize: 12, color: isOverdue ? C.danger : C.textLight, fontWeight: isOverdue ? 700 : 400 }}>
                        {isOverdue ? "متأخر — " : ""}{item.date}
                      </span>
                    </div>
                    <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>{item.type} — {item.subtitle}</p>
                  </div>
                </div>
              </Card>
            );
          })}
          {upcoming.length === 0 && <Card><p style={{ textAlign: "center", color: C.textLight, padding: 30 }}>لا توجد مواعيد قادمة</p></Card>}
        </div>
      </div>
    );
  }

  // ----------- Attachments Tab -----------
  function renderAttachments() {
    const allAttachments = [
      ...donors.flatMap(d => (d.attachments || []).map(a => ({ file: a, source: d.name, type: "جهة مانحة" }))),
      ...reports.filter(r => r.link).map(r => ({ file: r.link, source: r.projectName, type: `تقرير ${r.type}` })),
    ];

    return (
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 18px", display: "flex", alignItems: "center", gap: 8 }}>
          <Paperclip size={18} color={C.teal} /> جميع المرفقات ({allAttachments.length})
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
          {allAttachments.map((a, i) => (
            <Card key={i} style={{ padding: 16, display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: `${C.primary}08`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Paperclip size={20} color={C.primary} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.file}</p>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: C.textLight }}>{a.source} — {a.type}</p>
              </div>
            </Card>
          ))}
          {allAttachments.length === 0 && <Card><p style={{ textAlign: "center", color: C.textLight, padding: 30 }}>لا توجد مرفقات</p></Card>}
        </div>
      </div>
    );
  }

  // ===== MODALS =====
  function renderDonorForm() {
    return (
      <Modal title={editItem ? "تعديل بيانات الجهة المانحة" : "إضافة جهة مانحة جديدة"} onClose={() => { setShowForm(null); setEditItem(null); }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <Field label="اسم الجهة المانحة" value={donorForm.name} onChange={v => setDonorForm(p => ({ ...p, name: v }))} required style={{ gridColumn: "1 / -1" }} />
          <Field label="نوع الجهة" type="select" value={donorForm.type} onChange={v => setDonorForm(p => ({ ...p, type: v }))} options={DONOR_TYPES} required />
          <Field label="حالة العلاقة" type="select" value={donorForm.status} onChange={v => setDonorForm(p => ({ ...p, status: v }))} options={DONOR_STATUSES} />
          <Field label="مسؤول التواصل" value={donorForm.contact} onChange={v => setDonorForm(p => ({ ...p, contact: v }))} />
          <Field label="رقم الجوال" value={donorForm.phone} onChange={v => setDonorForm(p => ({ ...p, phone: v }))} />
          <Field label="البريد الإلكتروني" type="email" value={donorForm.email} onChange={v => setDonorForm(p => ({ ...p, email: v }))} />
          <Field label="الموقع الإلكتروني" value={donorForm.website} onChange={v => setDonorForm(p => ({ ...p, website: v }))} />
          <Field label="تاريخ آخر تواصل" type="date" value={donorForm.lastContact} onChange={v => setDonorForm(p => ({ ...p, lastContact: v }))} />
          <Field label="تاريخ المتابعة القادمة" type="date" value={donorForm.nextFollowUp} onChange={v => setDonorForm(p => ({ ...p, nextFollowUp: v }))} />
          <Field label="ملاحظات" type="textarea" value={donorForm.notes} onChange={v => setDonorForm(p => ({ ...p, notes: v }))} style={{ gridColumn: "1 / -1" }} />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <Btn variant="ghost" onClick={() => { setShowForm(null); setEditItem(null); }}>إلغاء</Btn>
          <Btn onClick={handleSaveDonor}>{editItem ? "تحديث" : "حفظ"}</Btn>
        </div>
      </Modal>
    );
  }

  function renderGrantForm() {
    return (
      <Modal title={editItem ? "تعديل بيانات المنحة" : "إضافة منحة / مشروع جديد"} onClose={() => { setShowForm(null); setEditItem(null); }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <Field label="اسم المشروع" value={grantForm.projectName} onChange={v => setGrantForm(p => ({ ...p, projectName: v }))} required style={{ gridColumn: "1 / -1" }} />
          <Field label="الجهة المانحة" type="select" value={grantForm.donorId} onChange={v => setGrantForm(p => ({ ...p, donorId: v }))} options={donors.map(d => ({ value: d.id, label: d.name }))} required />
          <Field label="رقم المنحة" value={grantForm.grantNumber} onChange={v => setGrantForm(p => ({ ...p, grantNumber: v }))} />
          <Field label="تاريخ اعتماد المنحة" type="date" value={grantForm.approvalDate} onChange={v => setGrantForm(p => ({ ...p, approvalDate: v }))} />
          <Field label="مبلغ المنحة (ريال)" type="number" value={grantForm.amount} onChange={v => setGrantForm(p => ({ ...p, amount: Number(v) || 0 }))} />
          <Field label="المبلغ المصروف" type="number" value={grantForm.spent} onChange={v => setGrantForm(p => ({ ...p, spent: Number(v) || 0 }))} />
          <Field label="مدة التنفيذ" value={grantForm.duration} onChange={v => setGrantForm(p => ({ ...p, duration: v }))} placeholder="مثال: 12 شهر" />
          <Field label="حالة المشروع" type="select" value={grantForm.status} onChange={v => setGrantForm(p => ({ ...p, status: v }))} options={PROJECT_STATUSES} />
          <Field label="تاريخ البداية" type="date" value={grantForm.startDate} onChange={v => setGrantForm(p => ({ ...p, startDate: v }))} />
          <Field label="تاريخ النهاية" type="date" value={grantForm.endDate} onChange={v => setGrantForm(p => ({ ...p, endDate: v }))} />
          <Field label="نسبة الإنجاز (%)" type="number" value={grantForm.progress} onChange={v => setGrantForm(p => ({ ...p, progress: Number(v) || 0 }))} min="0" max="100" />
          <Field label="مسؤول المشروع" value={grantForm.manager} onChange={v => setGrantForm(p => ({ ...p, manager: v }))} />
          <Field label="شروط الجهة المانحة" type="textarea" value={grantForm.conditions} onChange={v => setGrantForm(p => ({ ...p, conditions: v }))} style={{ gridColumn: "1 / -1" }} />
          <Field label="ملاحظات التنفيذ" type="textarea" value={grantForm.notes} onChange={v => setGrantForm(p => ({ ...p, notes: v }))} style={{ gridColumn: "1 / -1" }} />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <Btn variant="ghost" onClick={() => { setShowForm(null); setEditItem(null); }}>إلغاء</Btn>
          <Btn onClick={handleSaveGrant}>{editItem ? "تحديث" : "حفظ"}</Btn>
        </div>
      </Modal>
    );
  }

  function renderReportForm() {
    return (
      <Modal title={editItem ? "تعديل التقرير" : "إضافة تقرير جديد"} onClose={() => { setShowForm(null); setEditItem(null); }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <Field label="المشروع" type="select" value={reportForm.grantId} onChange={v => setReportForm(p => ({ ...p, grantId: v }))} options={grants.map(g => ({ value: g.id, label: g.projectName }))} required />
          <Field label="نوع التقرير" type="select" value={reportForm.type} onChange={v => setReportForm(p => ({ ...p, type: v }))} options={REPORT_TYPES} required />
          <Field label="تاريخ الاستحقاق" type="date" value={reportForm.dueDate} onChange={v => setReportForm(p => ({ ...p, dueDate: v }))} required />
          <Field label="تاريخ الرفع" type="date" value={reportForm.submitDate} onChange={v => setReportForm(p => ({ ...p, submitDate: v }))} />
          <Field label="حالة التقرير" type="select" value={reportForm.status} onChange={v => setReportForm(p => ({ ...p, status: v }))} options={REPORT_STATUSES} />
          <Field label="المسؤول عن التقرير" value={reportForm.responsible} onChange={v => setReportForm(p => ({ ...p, responsible: v }))} />
          <Field label="رابط أو اسم الملف" value={reportForm.link} onChange={v => setReportForm(p => ({ ...p, link: v }))} />
          <Field label="ملاحظات الجهة المانحة" type="textarea" value={reportForm.donorNotes} onChange={v => setReportForm(p => ({ ...p, donorNotes: v }))} style={{ gridColumn: "1 / -1" }} />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <Btn variant="ghost" onClick={() => { setShowForm(null); setEditItem(null); }}>إلغاء</Btn>
          <Btn onClick={handleSaveReport}>{editItem ? "تحديث" : "حفظ"}</Btn>
        </div>
      </Modal>
    );
  }

  // ===== MAIN RENDER =====
  return (
    <div>
      <ModuleHeader icon={Building2} title="إدارة الجهات المانحة والمنح" subtitle="إدارة ومتابعة الجهات المانحة والمنح والمشاريع والتقارير">
        <MonthlyReportButton moduleTitle="الجهات المانحة والمنح" sources={reportSources} />
      </ModuleHeader>
      <TabBar tabs={tabs} active={tab} onChange={t => { setTab(t); setSearchQuery(""); setFilterStatus(""); setFilterType(""); setViewDonor(null); setViewGrant(null); }} />

      {tab === "overview" && renderOverview()}
      {tab === "donors" && renderDonors()}
      {tab === "grants" && renderGrants()}
      {tab === "reports" && renderReports()}
      {tab === "followups" && renderFollowUps()}
      {tab === "attachments" && renderAttachments()}
      {tab === "meetings" && <MeetingsTab user={user} departmentKey="grants" module="grants" />}
      {tab === "finance" && <TransactionsTab budget={500000} module="grants" />}

      {showForm === "donor" && renderDonorForm()}
      {showForm === "grant" && renderGrantForm()}
      {showForm === "report" && renderReportForm()}

      {ToastView}
    </div>
  );
}
